import { AuthenticationService } from 'src/app/core/services/auth.service';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { ConfigSystemParams, DgColumnDataType, DgColumnWidths, ErrorStrings, eSimApiPaths } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { GridViewDefaults } from 'src/app/core/grid-view/component/grid-view.defaults';
import { DgActionColumn } from 'src/app/core/interfaces/datagrid/dg-action-column';
import { DgApiPath } from 'src/app/core/interfaces/datagrid/dg-api-paths';
import { DgColumn } from 'src/app/core/interfaces/datagrid/dg-column';
import { DgPopup } from 'src/app/core/interfaces/datagrid/dg-pop-up';
import { DgProperties } from 'src/app/core/interfaces/datagrid/dg-properties';
import { EncryptionService } from 'src/app/core/services/utelize/general/encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { firstValueFrom } from 'rxjs';
import { GridViewComponent } from 'src/app/core/grid-view/component/grid-view.component';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';

const apiPath             =environment.eSimApi+eSimApiPaths.Webbing;
const exportFilenameSuffix="SIM-Log-Data";
const popUpTitle          ="SIM Log";

@Component({
  selector: 'app-sim-esim-log-embedded',
  templateUrl: './sim-esim-log.component.html',
  styleUrls: ['./sim-esim-log.component.scss']
})

export class SimEsimLogEmbeddedComponent implements OnInit {
  
  @Input() iccid  : string | undefined;
  @Input() orderid: number | undefined;
  
  @ViewChild('gridView', { static: false }) gridView!: GridViewComponent;
  
  private gridViewDefaults!: GridViewDefaults;
  
  public curUser        : SystemUserDTO;
  public dgApiPath!     : DgApiPath | undefined;
  public dgActionColumn!: DgActionColumn | undefined;
  public dgColumns!     : Array<DgColumn>;
  public dgPopup!       : DgPopup | undefined;
  public dgProperties!  : DgProperties | undefined;
  public gotDgProperties: boolean=false;
  
  constructor(
    private authenticationService     : AuthenticationService,
    private componentAccessUserService: ComponentAccessUserService,
    private configSystemService       : ConfigSystemService,
    private encryptionService         : EncryptionService,
    private exceptionsService         : ExceptionsService
  ) {
    this.curUser         =this.authenticationService.currentUserObject();
    this.gridViewDefaults=new GridViewDefaults(this.authenticationService, this.componentAccessUserService, this.configSystemService, this.exceptionsService);
  }

  public async ngOnInit(): Promise<void> {
    try {
      if (this.iccid == undefined) {
        throw new Error("SimNetworkEventsEmbeddedComponent - ICCID cannot be determined");
      }

      await this.setUpDgApiPaths();
      this.setUpDgActionColumn();
      this.setUpDgColumns();
      this.setUpDgPopup();
      this.setUpDgProperties();

      this.gotDgProperties=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private async setUpDgApiPaths(): Promise<void> {
    try {
      this.dgApiPath            =this.gridViewDefaults.setUpDgApiPaths(apiPath);
      this.dgApiPath!.refreshGet=() => this.setUpDgApiPathGet();

      await this.setUpDgApiPathGet();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async setUpDgApiPathGet(): Promise<void> {
    try {
      const encResult =await firstValueFrom(this.encryptionService.encrypt_AesServerSideForWeb(<string>this.iccid, true));
      const iccidEncSS=encResult.result;

      this.dgApiPath!.get=environment.eSimApi +
                          eSimApiPaths.Webbing + '/' +
                          eSimApiPaths.WebbingGetESIMLogEventLog +
                          "?iccidEncSS=" + iccidEncSS +
                          "&orderid=" + this.orderid;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgActionColumn() {
    try {
      this.dgActionColumn         =this.gridViewDefaults.setUpDgActionColumn();
      this.dgActionColumn!.visible=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgPopup() {
    try {
      this.dgPopup=this.gridViewDefaults.setUpDgPopup(popUpTitle);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgProperties() {
    try {
      this.dgProperties                  =this.gridViewDefaults.setUpDgProperties("", exportFilenameSuffix + "-" + this.iccid);
      // this.dgProperties!.errorReplaceIf  =ErrorStrings.OriginalWebbingError;
      // this.dgProperties!.errorReplacement=ErrorStrings.ReplacementWebbingError;
      this.dgProperties!.errorSuppressIf =ErrorStrings.OriginalWebbingError;
      this.dgProperties!.gridKey         ="EmbeddedSimEsimLog";
      this.dgProperties!.showAddButton   =false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgColumns() {
    try {
      let dgColumn: DgColumn | undefined;
      
      //ID
      dgColumn             =this.gridViewDefaults.setUpDgColumn("id", "ID", 0, DgColumnDataType.number);
      dgColumn!.gridVisible=this.configSystemService.getValueAsBoolean(ConfigSystemParams.GridShowId, false);
      dgColumn!.width      =DgColumnWidths.id;
      this.dgColumns       =[dgColumn!];

      //CustomerID
      // dgColumn=this.gridViewDefaults.setUpDgColumn("customerID", "Customer ID", "", DgColumnDataType.number);
      // this.dgColumns.push(dgColumn!);

      //ICCID
      dgColumn=this.gridViewDefaults.setUpDgColumn("iccid", "ICCID", "", DgColumnDataType.string);
      this.dgColumns.push(dgColumn!);

      //SDID
      // dgColumn=this.gridViewDefaults.setUpDgColumn("sdid", "SDID", "", DgColumnDataType.number);
      // this.dgColumns.push(dgColumn!);

      //Notification
      dgColumn=this.gridViewDefaults.setUpDgColumn("notification", "Notification", "", DgColumnDataType.string);
      this.dgColumns.push(dgColumn!);

      //Date
      dgColumn=this.gridViewDefaults.setUpDgColumn("date", "Timestamp", "", DgColumnDataType.datetime);
      this.dgColumns.push(dgColumn!);

      //UpdatedBy
      dgColumn=this.gridViewDefaults.setUpDgColumn("updatedBy", "Updated By", "", DgColumnDataType.string);
      this.dgColumns.push(dgColumn!);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
